import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import { determineFunnelKey } from "src/lib/determineFunnelKey";
import {
  ItemType,
  SubscriptionInterval
} from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";

export interface PromptsState {
  emailVerifyPromptVisible: boolean;
  subscriptionInactivePromptVisible: boolean;
  subscriptionPausedPromptVisible: boolean;
  subscriptionExpiredItemsPromptVisible: boolean;
  reEnrollUrl: string;
}

export interface PromptsDependencies {
  pathname: string;
  isMobile: boolean;
  emailVerified: boolean;
  userHasEmail: boolean;
  lastSignupFunnel: string | undefined;
  hasActiveSubscription: boolean;
  hasFinishedSubscription: boolean;
  allSubscriptions: SubscriptionDetailsResponse[];
  getSubscriptionPurchaseItems: (
    subscriptionId: string,
    itemType: ItemType
  ) => Array<{ expiredAt?: string }>;
  queryString?: string;
}

const DEFAULT_PROMPT_PATHS = [
  Path.profile,
  Path.labResults,
  Path.myPlan,
  Path.chat
];

export default class PromptsCubit extends Cubit<PromptsState> {
  static isolated = true;

  constructor() {
    super({
      emailVerifyPromptVisible: false,
      subscriptionInactivePromptVisible: false,
      subscriptionPausedPromptVisible: false,
      subscriptionExpiredItemsPromptVisible: false,
      reEnrollUrl: ""
    });
  }

  private canDisplayOnPage(
    pathname: string,
    isMobile: boolean,
    additionalPaths: Path[] = []
  ): boolean {
    const allowedPaths = [...DEFAULT_PROMPT_PATHS, ...additionalPaths];
    return allowedPaths.some((path) => {
      const appPath = createAppPath(path, "app");
      if (pathname === appPath) {
        return true;
      }
      // On mobile, profile sub-routes overlay the dashboard so prompts stay visible
      if (pathname.startsWith(`${appPath}/`) && isMobile) {
        return true;
      }
      return false;
    });
  }

  private isSubscriptionPaused(
    subscriptions: SubscriptionDetailsResponse[]
  ): boolean {
    return subscriptions.some(
      (sub) =>
        sub.status === SubscriptionDetailsResponse.status.PAUSED &&
        (sub.renewalInterval === SubscriptionInterval.monthly ||
          sub.renewalInterval === SubscriptionInterval.quarterly)
    );
  }

  private hasExpiredItems(
    subscriptions: SubscriptionDetailsResponse[],
    getItems: PromptsDependencies["getSubscriptionPurchaseItems"]
  ): boolean {
    const activeOrReviewSubs = subscriptions.filter(
      (sub) =>
        sub.status === SubscriptionDetailsResponse.status.ACTIVE ||
        sub.status === SubscriptionDetailsResponse.status.IN_REVIEW
    );

    return activeOrReviewSubs.some((subscription) => {
      const items = getItems(subscription.id, ItemType.MEDICATION_SUGGESTION);
      return items.some((item) => item.expiredAt);
    });
  }

  evaluate(deps: PromptsDependencies): void {
    const {
      pathname,
      isMobile,
      emailVerified,
      userHasEmail,
      lastSignupFunnel,
      hasActiveSubscription,
      hasFinishedSubscription,
      allSubscriptions,
      getSubscriptionPurchaseItems,
      queryString
    } = deps;

    const canBeDisplayedOnCurrentPage = this.canDisplayOnPage(
      pathname,
      isMobile
    );
    const canBeDisplayedOnHomePage = this.canDisplayOnPage(pathname, isMobile, [
      Path.home
    ]);

    const isSubscriptionPaused = this.isSubscriptionPaused(allSubscriptions);
    const subscriptionContainsExpiredItems = this.hasExpiredItems(
      allSubscriptions,
      getSubscriptionPurchaseItems
    );

    const subscriptionExpiredItemsPromptVisible =
      canBeDisplayedOnCurrentPage && subscriptionContainsExpiredItems;

    const subscriptionPausedPromptVisible =
      canBeDisplayedOnCurrentPage && isSubscriptionPaused;

    const emailVerifyPromptVisible =
      canBeDisplayedOnCurrentPage &&
      !subscriptionPausedPromptVisible &&
      !subscriptionExpiredItemsPromptVisible &&
      !emailVerified &&
      userHasEmail;

    const subscriptionInactivePromptVisible =
      canBeDisplayedOnHomePage &&
      hasFinishedSubscription &&
      !hasActiveSubscription;

    const draftSubscription = allSubscriptions.find(
      (s) => s.status === SubscriptionDetailsResponse.status.DRAFT
    );
    const funnelKey = determineFunnelKey({
      draftSubscription,
      lastSignupFunnel
    });
    const reEnrollUrl = `/signup/${funnelKey}${queryString ? `?${queryString}` : ""}`;

    this.emit({
      emailVerifyPromptVisible,
      subscriptionInactivePromptVisible,
      subscriptionPausedPromptVisible,
      subscriptionExpiredItemsPromptVisible,
      reEnrollUrl
    });
  }
}
